import { getShipmentValues } from "./shipment";
import { getActiveSortedPalletTypes } from "./palletHelpers";
import { getActiveSortedUnitTypes } from "./unitHelpers";
import {
  HUNDREDWEIGHT,
  BY_THE_PALLET_WAREHOUSE_BUILT,
  BY_THE_PALLET,
  BY_THE_UNIT_ITEM,
  BY_THE_VAULT,
  FLAT_RATE,
  SQUARE_FOOT_LOCATION,
  TIME_AND_MATERIALS_BLENDED,
  TIME_AND_MATERIALS_ITEMIZED,
} from "../components/project/settings/utils/billingTypes";

export function getBillingQuantityOptions(billingType, palletTypes = [], unitTypes = []) {
  switch (billingType) {
    case BY_THE_PALLET_WAREHOUSE_BUILT:
      if (palletTypes && palletTypes.length > 0) {
        return getActiveSortedPalletTypes(palletTypes)
          .filter(pt => pt.id && pt.id !== "minimum_charge")
          .map(pt => [pt.id, pt.name]);
      }
      return [
        ["normal_pallet", "Standard Pallet"],
        ["oversized_pallet", "Oversized Pallet"],
        ["double_oversized_pallet", "Double Oversized Pallet"],
      ];
    case BY_THE_PALLET:
      if (palletTypes && palletTypes.length > 0) {
        return getActiveSortedPalletTypes(palletTypes)
          .filter(pt => pt.id)
          .map(pt => [pt.id, pt.name]);
      }
      return [
        ["normal_pallet", "Standard Pallet"],
        ["oversized_pallet", "Oversized Pallet"],
        ["double_oversized_pallet", "Double Oversized Pallet"],
        ["minimum_charge", "Minimum Charge"],
      ];
    case BY_THE_UNIT_ITEM:
      if (unitTypes && unitTypes.length > 0) {
        return getActiveSortedUnitTypes(unitTypes)
          .filter(ut => ut.id)
          .map(ut => [ut.id, ut.name]);
      }
      return [["standard_unit", "Standard Unit"]];
    case BY_THE_VAULT:
      return [["per_vault", "Vaults Used"]];
    case HUNDREDWEIGHT:
      return [["hundredweight", "Weight"]];
    case TIME_AND_MATERIALS_BLENDED:
      return [["time_and_materials_rate", "Total Hours"]];
    case TIME_AND_MATERIALS_ITEMIZED:
      return [
        ["installer_rates", "Installer Hours"],
        ["warehouse_labor_rates", "Warehouse Labor Hours"],
        ["fork_lift_rates", "Forklift Hours"],
      ];
    default:
      return [];
  }
}

export function getBillingQuantityLabels(billingType, flow = "Inbound") {
  switch (billingType) {
    case BY_THE_PALLET_WAREHOUSE_BUILT:
      return {
        title: `${flow} Billable Quantities`,
        subtitle: "Confirm the number and type of pallets built from loose cartons/items.",
        quantityLabel: "# Built",
        typeLabel: "Pallet Type",
      };
    case BY_THE_PALLET:
      return {
        title: `${flow} Billable Quantities`,
        subtitle:
          "Confirm the number and type of pallets received. Select minimum charge for anything smaller than a pallet.",
        quantityLabel: "# Received",
        typeLabel: "Pallet Type",
      };
    case BY_THE_UNIT_ITEM:
      return {
        title: `${flow} Billable Quantities`,
        subtitle: "Confirm the number of units or items received.",
        quantityLabel: "# of Units",
        typeLabel: "Unit Type",
      };
    case BY_THE_VAULT:
      return {
        title: `${flow} Billable Quantities`,
        subtitle: "Confirm the number of vaults used for this transaction.",
        quantityLabel: "# of Vaults",
        typeLabel: "Vault",
      };
    case SQUARE_FOOT_LOCATION:
      return {
        title: `${flow} Billable Quantities`,
        subtitle:
          "Square footage is calculated from the picked warehouse locations below. Use the location links to review or update the source location details.",
        quantityLabel: "Square Feet",
        typeLabel: "Location",
      };
    case HUNDREDWEIGHT:
      return {
        title: `${flow} Billable Quantities`,
        subtitle:
          "Confirm the total weight for this inbound shipment. It can be usually found on your BOL, packing slip, or other documentation.",
        quantityLabel: "Total Weight",
        typeLabel: "Weight",
      };
    case TIME_AND_MATERIALS_BLENDED:
      return {
        title: `${flow} Billable Quantities`,
        subtitle: "Confirm the total hours worked for this transaction.",
        quantityLabel: "Hours",
        typeLabel: "Labor Type",
      };
    case TIME_AND_MATERIALS_ITEMIZED:
      return {
        title: `${flow} Billable Quantities`,
        subtitle: "Confirm hours by labor type for this transaction.",
        quantityLabel: "Hours",
        typeLabel: "Labor Type",
      };
    default:
      return {
        title: `${flow} Billable Quantities`,
        subtitle: "",
        quantityLabel: "Quantity",
        typeLabel: "Type",
      };
  }
}

export function extractBillingTypes(billingValue, legacyBillingType = null) {
  // Handle new structure with types and comments
  if (typeof billingValue === "object" && billingValue?.types) {
    return {
      types: billingValue.types,
      comments: billingValue.comments || {},
    };
  }

  // Handle legacy array format
  if (Array.isArray(billingValue)) {
    return { types: billingValue, comments: {} };
  }

  // Handle legacy single value or fallback
  const legacyTypes = [legacyBillingType, billingValue].filter(Boolean);
  return { types: legacyTypes, comments: {} };
}

// Flat rate has no billable quantities, so when a project also lists another
// type for the category, that type is the one the quantities are entered for.
// Mirrors Billing::ChargesCalculation::Service#quantity_billing_type.
function getQuantityBillingType(project, category) {
  const { types = [] } = extractBillingTypes(project?.billingTypes?.[category], project?.billingType);
  return types.find(type => type && type !== FLAT_RATE) || types[0];
}

export function getOutboundBillingType(project) {
  return getQuantityBillingType(project, "outbound");
}

export function getInboundBillingType(project) {
  return getQuantityBillingType(project, "inbound");
}

export function getBillingTypesArray(billingValue) {
  if (!billingValue) return [];

  // Handle new structure with types and comments
  if (typeof billingValue === "object" && billingValue.types) {
    return billingValue.types;
  }

  // Handle array format
  if (Array.isArray(billingValue)) {
    return billingValue;
  }

  // Single value
  return [billingValue];
}

export function prepareShipmentUpdateInput(shipment, additionalFields = {}) {
  const shipmentValues = getShipmentValues(shipment);
  const allFields = { ...shipmentValues, ...additionalFields };

  // Filter out null and undefined values
  return Object.fromEntries(
    Object.entries(allFields).filter(([key, value]) => value !== null && value !== undefined)
  );
}

export function addShipmentBillingToAssetInput(shipment, baseInput = {}) {
  return {
    ...baseInput,
    ...(shipment.costCenter?.id && { costCenterId: parseInt(shipment.costCenter.id, 10) }),
    ...(shipment.billingType && { billingType: shipment.billingType }),
  };
}

export function billingQuantitiesInput(billingType, totalWeight, weightUnits, pallets) {
  const updateData = { billingType: billingType };

  if (billingType === HUNDREDWEIGHT) {
    // Convert Kgs to Lbs if shipment.total_weight is in Kgs
    const numericWeight = parseFloat(totalWeight) || 0;
    const convertedWeight = weightUnits === "Kgs" ? numericWeight * 2.20462 : numericWeight;
    updateData.totalWeight = convertedWeight;

    // Always store weight units as "Lbs" since we convert Kgs to Lbs
    updateData.weightUnits = weightUnits === "Kgs" ? "Lbs" : weightUnits;
  } else {
    // Filter out entries with 0 count for multi-option billing types
    // (users can set unused options to 0)
    updateData.billingQuantities = pallets
      .filter(p => p.pallet_count > 0)
      .map(p => ({ quantity_type: p.pallet_type, quantity_count: p.pallet_count }));
  }

  return updateData;
}

export async function updateShipmentBillingType(
  updateShipment,
  shipment,
  billingType,
  additionalFields = {}
) {
  const input = prepareShipmentUpdateInput(shipment, {
    billingType,
    ...additionalFields,
  });

  return updateShipment({
    variables: {
      id: shipment.id,
      input,
    },
  });
}
